import React from 'react';
import { useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import SegmentIcon from '@mui/icons-material/Segment';
import PersonIcon from '@mui/icons-material/Person';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import TimelineIcon from '@mui/icons-material/Timeline';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
  getProjectUrl,
  getNotesUrl,
  getCorkboardsUrl,
  getTimelinesUrl,
  getCharactersUrl,
} from '../../urls';
import projectService from '../../utils/apiRequests/projectService';
import { getProjectTypeConfig } from '../../utils/projectTypeConfig';
import { useCommandSource } from '../../components/CommandPalette';

/** Poll for a DOM element; resolves null on timeout. */
const waitForElement = (elementId, timeoutMs = 4000) =>
  new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      const element = document.getElementById(elementId);
      if (element) {
        resolve(element);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });

const snippet = (text, max = 80) => {
  const clean = (text || '').trim().replace(/\s+/g, ' ');
  if (!clean) return '';
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * Registers the project-scoped command palette sources while a project is
 * open: its chapters, scenes, characters, notes, section navigation, and
 * common actions — all using the project type's own terminology.
 *
 * Split into a sync source (instant) and an async scene source (fetched from
 * the thin scene index endpoint) so opening the palette is never blocked by
 * the network.
 */
const useProjectCommands = ({
  activeProject,
  projectId,
  activeCenterSection,
  handleChapterClick,
  handleSceneClick,
  handleAddChapter,
  handleAddCharacter,
  handleAddNote,
  handleImportDocument,
  handleExportProject,
  handleEditProject,
  handleShareProject,
}) => {
  const navigate = useNavigate();

  const typeConfig = getProjectTypeConfig(activeProject?.type);
  const chapters = activeProject?.thinChapters || [];
  const role = activeProject?.myRole || 'owner';
  const isOwner = role === 'owner';
  const canEdit = isOwner || role === 'editor';
  const canComment = role !== 'viewer';

  /** Ensure the manuscript section is visible, then run the scroll handler. */
  const ensureManuscript = (chapterId, action) => {
    if (activeCenterSection !== 'manuscript') {
      navigate(getProjectUrl(projectId));
    }
    waitForElement(`chapter-${chapterId}`).then(() => action());
  };

  // While switching projects, activeProject can briefly belong to the previous
  // project — contribute nothing until the loaded project matches the URL.
  const isProjectReady = Boolean(activeProject && activeProject._id === projectId);

  useCommandSource(`project:${projectId}`, () => {
    if (!isProjectReady) return [];

    const items = [];

    // ---- Structure: chapters (or poems/acts/sections per project type)
    chapters.forEach((chapter) => {
      const chapterId = chapter.id || chapter._id;
      if (!chapterId) return;
      const wordCount = typeof chapter.wordCount === 'number' ? chapter.wordCount : null;
      items.push({
        id: `project:${projectId}:chapter:${chapterId}`,
        group: typeConfig.unitPlural || `${typeConfig.unitLabel}s`,
        title: chapter.name || `Untitled ${typeConfig.unitLabel}`,
        subtitle:
          wordCount !== null ? `${wordCount.toLocaleString()} words` : snippet(chapter.description),
        icon: <DescriptionIcon />,
        perform: () => ensureManuscript(chapterId, () => handleChapterClick(chapter)),
      });
    });

    // ---- Characters
    if (typeConfig.showCharacters) {
      const characters =
        activeProject.thinCharacters ||
        activeProject.characters ||
        activeProject.characterList ||
        [];
      characters.forEach((character) => {
        const characterId = character.id || character._id;
        const name = character.fullName || character.name;
        if (!characterId || !name) return;
        items.push({
          id: `project:${projectId}:character:${characterId}`,
          group: 'Characters',
          title: name,
          subtitle: snippet(character.description || character.role),
          icon: <PersonIcon />,
          perform: () => navigate(getCharactersUrl(projectId)),
        });
      });
    }

    // ---- Notes
    (activeProject.thinNotes || []).forEach((note) => {
      const noteId = note.id || note._id;
      const title = note.title || note.name || snippet(note.content, 40);
      if (!noteId || !title) return;
      items.push({
        id: `project:${projectId}:note:${noteId}`,
        group: 'Notes',
        title,
        subtitle: snippet(note.content),
        icon: <StickyNote2Icon />,
        perform: () => navigate(`${getNotesUrl(projectId)}/list`),
      });
    });

    // ---- Section navigation
    const sections = [
      {
        id: 'manuscript',
        title: typeConfig.sectionLabel,
        icon: <MenuBookIcon />,
        url: getProjectUrl(projectId),
        keywords: 'manuscript writing chapters scenes',
      },
      {
        id: 'notes',
        title: 'Notes',
        icon: <StickyNote2Icon />,
        url: getNotesUrl(projectId),
        keywords: 'notes ideas',
      },
      {
        id: 'corkboards',
        title: 'Corkboards',
        icon: <DashboardCustomizeIcon />,
        url: getCorkboardsUrl(projectId),
        keywords: 'corkboard cards board',
      },
      {
        id: 'timelines',
        title: 'Timelines',
        icon: <TimelineIcon />,
        url: getTimelinesUrl(projectId),
        keywords: 'timeline events chronology',
      },
    ];
    if (typeConfig.showCharacters) {
      sections.push({
        id: 'characters',
        title: 'Characters',
        icon: <GroupsIcon />,
        url: getCharactersUrl(projectId),
        keywords: 'characters cast npc',
      });
    }
    if (typeConfig.showGameDesign) {
      [
        { tool: 'erd', label: 'Game Design — ERD' },
        { tool: 'types', label: 'Game Design — Type Chart' },
        { tool: 'formulas', label: 'Game Design — Formulas' },
      ].forEach(({ tool, label }) => {
        sections.push({
          id: `gamedesign-${tool}`,
          title: label,
          icon: <SportsEsportsIcon />,
          url: `${getProjectUrl(projectId)}/gamedesign/${tool}`,
          keywords: 'game design systems',
        });
      });
    }
    sections.forEach((section) => {
      items.push({
        id: `project:${projectId}:goto:${section.id}`,
        group: 'Go to',
        title: section.title,
        keywords: section.keywords,
        icon: section.icon,
        perform: () => navigate(section.url),
      });
    });

    // ---- Actions (modals / panels already wired into the project view)
    // Only offer actions the user's role allows (server enforces regardless).
    const actions = [];
    if (canEdit) {
      actions.push({
        id: 'add-chapter',
        title: `Add ${typeConfig.unitLabel}`,
        keywords: 'new create chapter',
        icon: <AddIcon />,
        perform: handleAddChapter,
      });
      if (typeConfig.showCharacters) {
        actions.push({
          id: 'add-character',
          title: 'Add Character',
          keywords: 'new create character cast',
          icon: <AddIcon />,
          perform: handleAddCharacter,
        });
      }
      actions.push({
        id: 'import',
        title: 'Import Document',
        subtitle: 'Word (.docx)',
        keywords: 'import upload docx word manuscript',
        icon: <UploadIcon />,
        perform: handleImportDocument,
      });
    }
    if (canComment) {
      actions.push({
        id: 'add-note',
        title: 'Add Note',
        keywords: 'new create note',
        icon: <AddIcon />,
        perform: handleAddNote,
      });
    }
    actions.push({
      id: 'export',
      title: 'Export Project…',
      subtitle: 'Word, Markdown, HTML, text',
      keywords: 'export download save docx markdown',
      icon: <DownloadIcon />,
      perform: handleExportProject,
    });
    if (handleShareProject) {
      actions.push({
        id: 'share',
        title: isOwner ? 'Share Project…' : 'View Collaborators',
        subtitle: isOwner ? 'Invite viewers, commenters, editors' : undefined,
        keywords: 'share invite collaborators permissions access',
        icon: <PeopleAltIcon />,
        perform: handleShareProject,
      });
    }
    if (isOwner) {
      actions.push({
        id: 'edit-project',
        title: 'Edit Project Details',
        keywords: 'rename title type status settings',
        icon: <EditIcon />,
        perform: handleEditProject,
      });
    }
    actions.forEach((action) => {
      items.push({
        id: `project:${projectId}:action:${action.id}`,
        group: 'Actions',
        title: action.title,
        subtitle: action.subtitle,
        keywords: action.keywords,
        icon: action.icon,
        perform: action.perform,
      });
    });

    return items;
  });

  // ---- Scenes (async: one thin request for every scene in the project)
  useCommandSource(`project:${projectId}:scenes`, async () => {
    if (!isProjectReady || chapters.length === 0) return [];

    const response = await projectService.getSceneIndex(projectId);
    const scenes = response?.data?.scenes || [];
    if (scenes.length === 0) return [];

    const chapterNames = new Map();
    chapters.forEach((chapter) => {
      const chapterId = chapter.id || chapter._id;
      if (chapterId) chapterNames.set(String(chapterId), chapter.name || '');
    });

    return scenes.map((scene) => {
      const sceneClickId = scene._id || scene.id;
      const chapterId = String(scene.chapterId || '');
      const chapterName = chapterNames.get(chapterId) || '';
      const description = snippet(scene.description, 60);
      let subtitle = chapterName;
      if (chapterName && description) subtitle = `${chapterName} — ${description}`;
      else if (description) subtitle = description;
      return {
        id: `project:${projectId}:scene:${sceneClickId}`,
        group: typeConfig.subUnitPlural,
        title: scene.name?.trim() ? scene.name : `Untitled ${typeConfig.subUnitLabel}`,
        subtitle,
        icon: <SegmentIcon />,
        perform: () => ensureManuscript(chapterId, () => handleSceneClick(sceneClickId, chapterId)),
      };
    });
  });
};

export default useProjectCommands;
